import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { ApiKeyRecord, AuthMeResponse, CreateApiKeyResponse } from '../types';
import { AuthHttpError, createApiKey, getMe, listApiKeys, login, logout, revokeApiKeyById, signup } from '../services/authApi';
import { notifyApiKeyCreated, notifyLogout, notifyTokens, subscribeHostMessages } from '../services/hostBridge';
import { trackAuthEvent } from '../services/authTelemetry';
import {
  clearStoredTokens,
  getRefreshAtMs,
  loadStoredTokens,
  saveStoredTokens,
  type StoredAuthTokens,
} from '../services/tokenStore';

export type AuthStatus = 'initializing' | 'anonymous' | 'authenticated';

export type AuthContextValue = {
  status: AuthStatus;
  tokens: StoredAuthTokens | null;
  me: AuthMeResponse | null;
  isLoading: boolean;
  error: string | null;

  refreshMe: () => Promise<void>;
  login: (params: { email: string; password: string }) => Promise<void>;
  signup: (params: { name: string; email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;

  listApiKeys: () => Promise<ApiKeyRecord[]>;
  createApiKey: () => Promise<CreateApiKeyResponse>;
  revokeApiKeyById: (id: number) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function toErrorMessage(err: unknown): string {
  if (err instanceof AuthHttpError) {
    const payload = err.payload as any;
    if (payload && typeof payload.error === 'string') return payload.error;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'unknown_error';
}

function useAuthContextValue(): AuthContextValue {
  const [tokens, setTokens] = useState<StoredAuthTokens | null>(() => loadStoredTokens());
  const [me, setMe] = useState<AuthMeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const didBootstrap = useRef(false);

  const syncTokens = useCallback(() => {
    setTokens(loadStoredTokens());
  }, []);

  const loadMeAndSyncTokens = useCallback(async () => {
    const nextMe = await getMe();
    setMe(nextMe);
    syncTokens();
  }, [syncTokens]);

  const refreshMe = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await loadMeAndSyncTokens();
      trackAuthEvent({ name: 'token_refresh_success' });
    } catch (err) {
      const errMsg = toErrorMessage(err);
      if (err instanceof AuthHttpError && err.status === 401) {
        clearStoredTokens();
        syncTokens();
        setMe(null);
        // Don't show "not_authenticated" as an error - it's an expected state
        // when tokens are expired/invalid; user just needs to log in again
      } else {
        setError(errMsg);
      }
      trackAuthEvent({ name: 'token_refresh_failure', error: errMsg });
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, [loadMeAndSyncTokens, syncTokens]);

  const doLogin = useCallback(
    async (params: { email: string; password: string }) => {
      setIsLoading(true);
      setError(null);
      try {
        await login(params);
        syncTokens();
        await loadMeAndSyncTokens();
        const stored = loadStoredTokens();
        if (stored?.userId) {
          trackAuthEvent({ name: 'login_success', userId: stored.userId });
        }
      } catch (err) {
        const errMsg = toErrorMessage(err);
        setError(errMsg);
        trackAuthEvent({ name: 'login_failure', error: errMsg });
        throw err;
      } finally {
        setIsInitialized(true);
        setIsLoading(false);
      }
    },
    [loadMeAndSyncTokens, syncTokens],
  );

  const doSignup = useCallback(
    async (params: { name: string; email: string; password: string }) => {
      setIsLoading(true);
      setError(null);
      try {
        await signup(params);
        syncTokens();
        await loadMeAndSyncTokens();
        const stored = loadStoredTokens();
        if (stored?.userId) {
          trackAuthEvent({ name: 'signup_success', userId: stored.userId });
        }
      } catch (err) {
        const errMsg = toErrorMessage(err);
        setError(errMsg);
        trackAuthEvent({ name: 'signup_failure', error: errMsg });
        throw err;
      } finally {
        setIsInitialized(true);
        setIsLoading(false);
      }
    },
    [loadMeAndSyncTokens, syncTokens],
  );

  const doLogout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await logout();
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      clearStoredTokens();
      syncTokens();
      setMe(null);
      setIsInitialized(true);
      setIsLoading(false);
      notifyLogout();
      trackAuthEvent({ name: 'logout' });
    }
  }, [syncTokens]);

  const doListApiKeys = useCallback(async () => {
    const res = await listApiKeys();
    syncTokens();
    return res.items;
  }, [syncTokens]);

  const doCreateApiKey = useCallback(async () => {
    const res = await createApiKey();
    syncTokens();
    notifyApiKeyCreated(res.api_key);
    trackAuthEvent({ name: 'api_key_created', scope: res.scope });
    return res;
  }, [syncTokens]);

  const doRevokeApiKeyById = useCallback(
    async (id: number) => {
      await revokeApiKeyById(id);
      syncTokens();
      trackAuthEvent({ name: 'api_key_revoked', keyId: id });
    },
    [syncTokens],
  );

  const status: AuthStatus = useMemo(() => {
    if (!isInitialized) return 'initializing';
    return tokens ? 'authenticated' : 'anonymous';
  }, [isInitialized, tokens]);

  useEffect(() => {
    if (didBootstrap.current) return;
    didBootstrap.current = true;
    if (!tokens) {
      setIsInitialized(true);
      return;
    }
    void refreshMe();
  }, [tokens, refreshMe]);

  useEffect(() => {
    if (!tokens) return;
    const refreshAtMs = getRefreshAtMs(tokens);
    const delayMs = Math.max(0, refreshAtMs - Date.now());
    const id = window.setTimeout(() => {
      void refreshMe();
    }, delayMs);
    return () => window.clearTimeout(id);
  }, [tokens, refreshMe]);

  useEffect(() => {
    notifyTokens(tokens);
  }, [tokens]);

  useEffect(() => {
    const unsubscribe = subscribeHostMessages(async (msg) => {
      if (msg.type === 'setTokens') {
        saveStoredTokens(msg.tokens);
        syncTokens();
        await refreshMe();
        return;
      }
      if (msg.type === 'clearTokens') {
        clearStoredTokens();
        syncTokens();
        setMe(null);
        return;
      }
      if (msg.type === 'requestRefresh') {
        await refreshMe();
        notifyTokens(loadStoredTokens());
        return;
      }
      if (msg.type === 'logout') {
        await doLogout();
      }
    });
    return unsubscribe;
  }, [doLogout, refreshMe, syncTokens]);

  return {
    status,
    tokens,
    me,
    isLoading,
    error,

    refreshMe,
    login: doLogin,
    signup: doSignup,
    logout: doLogout,

    listApiKeys: doListApiKeys,
    createApiKey: doCreateApiKey,
    revokeApiKeyById: doRevokeApiKeyById,
  };
}

export function AuthProvider(props: { children: React.ReactNode }) {
  const value = useAuthContextValue();
  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
